import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Paper,
  Typography,
  IconButton,
  Divider,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  useTheme,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { format, formatDistanceToNow } from "date-fns";
import { callAPI } from "../../functions/api";

const formatDateWithRelative = (val) => {
  if (!val) return { relative: "N/A", absolute: "N/A" };
  const date = new Date(val);
  return {
    relative: formatDistanceToNow(date, { addSuffix: true }),
    absolute: format(date, "yyyy-MM-dd HH:mm:ss"),
  };
};

const severityColor = (severity) => {
  switch (severity) {
    case 4:
      return "#BA1650"; // Critical
    case 3:
      return "#F9CA00"; // High
    case 2:
      return "#E97F03"; // Medium
    case 1:
      return "#70C930"; // Low
    case 0:
      return "#888"; // Info
    default:
      return "#999"; // Unknown
  }
};

const severityLabel = (severity) => {
  const labels = ["Info", "Low", "Medium", "High", "Critical"];
  return labels[severity] || "Unknown";
};

const stateLabel = (state) => {
  switch (state) {
    case 0:
      return "Open";
    case 1:
      return "Reopened";
    case 2:
      return "Fixed";
    default:
      return "Unknown";
  }
};

const VulnerabilityDetailsModal = ({ open, onClose, data }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [ec2Data, setEc2Data] = useState(null);
  const [datadog, setDatadog] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";

  const styles = {
    container: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      backgroundColor: isLight ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.8)",
      backdropFilter: "blur(4px)",
    },
    paper: {
      width: "92%",
      maxWidth: "1400px",
      maxHeight: "95vh",
      backgroundColor: isLight ? "#fff" : "#2a2a2a",
      color: isLight ? "#000" : "#fff",
      borderRadius: "10px",
      overflow: "hidden",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: isLight ? "#f5f5f5" : "#1f1f1f",
      px: 3,
      py: 2,
    },
    content: {
      padding: 3,
      overflowY: "auto",
      maxHeight: "calc(95vh - 160px)",
      height: "calc(95vh - 160px)",
    },
    sectionTitle: {
      fontSize: "13px",
      fontWeight: "bold",
      color: isLight ? theme.palette.primary.main : "#F9CA00",
      mb: 0.5,
      mt: 0.5,
    },
    table: {
      backgroundColor: isLight ? "#fafafa" : "#1a1a1a",
      border: `1px solid ${isLight ? "#ddd" : "#444"}`,
      "& .MuiTableCell-root": {
        borderBottom: `1px solid ${isLight ? "#e0e0e0" : "#333"}`,
      },
    },
    cellLabel: {
      color: isLight ? "#333" : "#bbb",
      fontWeight: "bold",
      width: "35%",
      borderRight: `1px solid ${isLight ? "#ccc" : "#444"}`,
      verticalAlign: "top",
      padding: "6px 8px",
      fontSize: "12px",
    },
    cellValue: {
      color: isLight ? "#000" : "#eee",
      padding: "6px 8px",
      fontSize: "12px",
    },
    text: {
      fontSize: 13,
      color: isLight ? "#333" : "#ccc",
      whiteSpace: "pre-wrap",
    },
    codeBlock: {
      backgroundColor: isLight ? "#f5f5f5" : "#1a1a1a",
      padding: 1.5,
      borderRadius: 1,
      fontFamily: "monospace",
      fontSize: 11,
      overflowX: "auto",
      border: `1px solid ${isLight ? "#ddd" : "#444"}`,
      maxHeight: "150px",
      overflowY: "auto",
    },
    loading: {
      padding: 6,
      display: "flex",
      justifyContent: "center",
    },
    tabs: {
      backgroundColor: isLight ? "#f5f5f5" : "#1f1f1f",
      px: 2,
    },
  };

  useEffect(() => {
    if (!data || !data.ec2InstanceId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const fetchEc2 = callAPI(`assets/ec2/ec2byid?id=${data.ec2InstanceId}`, "GET", null, true);

    const fetchDatadog = callAPI(
      `logging/datadog/hostbyinstanceid?instanceId=${data.ec2InstanceId}`,
      "GET",
      null,
      true
    );

    Promise.allSettled([fetchEc2, fetchDatadog])
      .then(([ec2Result, ddResult]) => {
        if (ec2Result.status === "fulfilled") {
          // Handle wrapped API response
          const ec2Value =
            ec2Result.value?.value !== undefined ? ec2Result.value.value : ec2Result.value;
          // Only set if we have actual data (not null)
          if (ec2Value && ec2Value !== null) {
            setEc2Data(ec2Value);
          }
        }
        if (ddResult.status === "fulfilled") {
          // Handle wrapped API response
          const ddValue =
            ddResult.value?.value !== undefined ? ddResult.value.value : ddResult.value;
          // Only set if we have actual data (not null)
          if (ddValue && ddValue !== null) {
            setDatadog(ddValue);
          }
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [data]);

  if (!data) return null;

  const formatDate = (val) => (val ? format(new Date(val), "yyyy-MM-dd HH:mm:ss") : "N/A");
  const firstFound = formatDateWithRelative(data.firstFound);
  const lastFound = formatDateWithRelative(data.lastFound);
  const lastFixed = formatDateWithRelative(data.lastFixed);
  const resurfaced = formatDateWithRelative(data.resurfacedDate);

  const section = (icon, title, rows) => (
    <Box sx={{ mb: 0.5 }}>
      <Typography sx={styles.sectionTitle}>
        {icon} {title}
      </Typography>
      <Table size="small" sx={{ ...styles.table, mb: 0.5 }}>
        <TableBody>
          {rows.map(([label, value], i) => (
            <TableRow key={i}>
              <TableCell sx={styles.cellLabel}>{label}</TableCell>
              <TableCell sx={styles.cellValue}>{value ?? "N/A"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );

  const renderEc2Details = () => {
    if (!ec2Data) {
      return <Typography sx={styles.text}>No EC2 instance data found.</Typography>;
    }

    const tags = ec2Data.tags || {};
    const projectName = tags["Project Name"] || "-";
    const serviceOwner = tags["Service Owner"] || "-";
    const envColor = (ec2Data.environment || "").toLowerCase().includes("prod")
      ? "#BA1650"
      : (ec2Data.environment || "").toLowerCase().includes("dev")
        ? "#70C930"
        : "#F9CA00";

    return (
      <Grid container spacing={2}>
        <Grid item xs={4}>
          {section("🔹", "Instance Information", [
            ["Instance ID", ec2Data.id],
            ["Name", ec2Data.name],
            ["Project Name", projectName],
            ["Service Owner", serviceOwner],
            [
              "Environment",
              <Chip
                label={ec2Data.environment}
                size="small"
                sx={{ backgroundColor: envColor, color: "#fff" }}
              />,
            ],
            ["OS", `${ec2Data.osName ?? "-"} ${ec2Data.osVersion ?? ""}`],
          ])}
        </Grid>
        <Grid item xs={4}>
          {section("📜", "Account Details", [
            ["AWS Account", ec2Data.accountName],
            ["Region", ec2Data.regionDisplayName],
            ["Instance Type", ec2Data.instanceType],
            ["Architecture", ec2Data.architecture],
          ])}
        </Grid>
        <Grid item xs={4}>
          {section("🌐", "Networking", [
            ["Public IP", ec2Data.publicDnsName || "N/A"],
            ["Private IP", ec2Data.privateDnsName || "N/A"],
            ["VPC ID", ec2Data.vpcId || "N/A"],
          ])}
        </Grid>
        <Grid item xs={12}>
          <Typography sx={styles.sectionTitle}>🏷️ Tags</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {Object.keys(tags).length > 0 ? (
              Object.entries(tags).map(([key, val], i) => (
                <Chip
                  key={i}
                  label={`${key}: ${val}`}
                  size="small"
                  sx={{
                    backgroundColor: isLight ? "#e0e0e0" : "#444",
                    color: isLight ? "#000" : "#fff",
                    fontSize: 11,
                  }}
                />
              ))
            ) : (
              <Typography sx={styles.text}>No tags assigned</Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderDatadog = () => {
    if (!datadog || (datadog.value && datadog.value == null)) {
      return <Typography sx={styles.text}>No Datadog data found.</Typography>;
    }

    const formatDate = (val) => (val ? format(new Date(val), "yyyy-MM-dd HH:mm:ss") : "N/A");

    return (
      <Grid container spacing={2}>
        <Grid item xs={4}>
          {section("📈", "Usage Metrics", [
            ["CPU Usage", datadog.cpuUtilization != null ? `${datadog.cpuUtilization}%` : "N/A"],
            [
              "Memory Usage",
              datadog.memoryUtilization != null ? `${datadog.memoryUtilization}%` : "N/A",
            ],
            ["Disk Usage", datadog.diskUtilization != null ? `${datadog.diskUtilization}%` : "N/A"],
          ])}
        </Grid>
        <Grid item xs={4}>
          {section("💾", "Capacity", [
            ["Memory Total", datadog.memoryTotal != null ? `${datadog.memoryTotal} MB` : "N/A"],
            ["Disk Total", datadog.diskTotal != null ? `${datadog.diskTotal} GB` : "N/A"],
          ])}
        </Grid>
        <Grid item xs={4}>
          {section("🕒", "Status", [
            ["Last Updated", datadog.lastUpdated ? formatDate(datadog.lastUpdated) : "N/A"],
            ["Status", datadog.status || "N/A"],
            ["Muted", datadog.isMuted ? "Yes" : "No"],
          ])}
        </Grid>
        <Grid item xs={4}>
          {section("🌍", "Cloud Metadata", [
            ["AWS Name", datadog.awsName || "N/A"],
            ["AWS Account", datadog.awsAccountName || "N/A"],
            ["Region", datadog.region || "N/A"],
            ["AZ", datadog.availabilityZone || "N/A"],
          ])}
        </Grid>
        <Grid item xs={4}>
          {section("🧩", "Platform Info", [
            ["Platform", datadog.platform || "N/A"],
            ["OS", datadog.os || "N/A"],
            ["Instance Type", datadog.instanceType || "N/A"],
          ])}
        </Grid>
      </Grid>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={styles.container}>
        <Paper sx={styles.paper}>
          <Box sx={styles.header}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "#F9CA00" }}>
                Vulnerability Details
              </Typography>
              <Typography variant="body2" sx={{ color: "#999", mt: 0.5 }}>
                {data.pluginName}
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon sx={{ color: "#fff" }} />
            </IconButton>
          </Box>

          {loading ? (
            <Box sx={styles.loading}>
              <CircularProgress sx={{ color: "#F9CA00" }} />
            </Box>
          ) : (
            <>
              <Tabs
                value={activeTab}
                onChange={(e, newTab) => setActiveTab(newTab)}
                textColor="inherit"
                indicatorColor="secondary"
                sx={styles.tabs}
              >
                <Tab label="Vulnerability" />
                {ec2Data && <Tab label="EC2 Instance" />}
                {datadog && <Tab label="Datadog Metrics" />}
              </Tabs>
              <Divider sx={{ backgroundColor: "#444" }} />

              <Box sx={styles.content}>
                {activeTab === 0 && (
                  <Grid container spacing={1.5}>
                    {/* Vulnerability Information */}
                    <Grid item xs={3}>
                      {section("🔐", "Vulnerability", [
                        [
                          "Severity",
                          <Chip
                            label={severityLabel(data.severity)}
                            size="small"
                            sx={{
                              backgroundColor: severityColor(data.severity),
                              color: "#fff",
                              fontWeight: "bold",
                              fontSize: "10px",
                            }}
                          />,
                        ],
                        [
                          "Status",
                          <Chip
                            label={stateLabel(data.tenableState)}
                            size="small"
                            sx={{
                              backgroundColor: data.tenableState === 0 ? "#BA1650" : "#4CAF50",
                              color: "#fff",
                              fontSize: "10px",
                            }}
                          />,
                        ],
                        ["CVSS", data.cvss3BaseScore || "N/A"],
                        ["Plugin ID", data.pluginId],
                      ])}
                    </Grid>

                    {/* Timeline */}
                    <Grid item xs={3}>
                      {section("🕒", "Timeline", [
                        ["First Found", firstFound.relative],
                        ["Last Found", lastFound.relative],
                        ["Last Fixed", data.lastFixed ? lastFixed.relative : "N/A"],
                        ["Last Updated", formatDate(data.lastUpdated)],
                      ])}
                    </Grid>

                    {/* Asset Information */}
                    <Grid item xs={3}>
                      {section("🖥️", "Asset", [
                        ["Name", data.tenableAssetName],
                        ["EC2 ID", data.ec2InstanceId],
                        ["OS", data.operatingSystemFamily === 1 ? "Linux" : "Windows"],
                        [
                          "Product",
                          [
                            "N3A",
                            "IRW",
                            "GNW",
                            "SVCOPS",
                            "IREvents",
                            "ARTFACT",
                            "SharedService",
                            "BrandLive",
                            "INXPO",
                            "JUMP",
                            "RPA",
                            "XRAY",
                            "SANDBOX",
                            "Informatica",
                            "MoveIT",
                            "Tableau",
                          ][data.ec2ProductType] || "Unknown",
                        ],
                      ])}
                    </Grid>

                    {/* Synopsis */}
                    <Grid item xs={3}>
                      {section("📋", "Synopsis", [["Description", data.synopsis]])}
                    </Grid>

                    {/* Description */}
                    <Grid item xs={12}>
                      <Typography sx={styles.sectionTitle}>📝 Description</Typography>
                      <Box sx={{ ...styles.codeBlock, maxHeight: "120px" }}>
                        <Typography sx={styles.text}>
                          {data.pluginDescription || "No description available"}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Solution */}
                    <Grid item xs={12}>
                      <Typography sx={styles.sectionTitle}>💡 Solution</Typography>
                      <Box sx={{ ...styles.codeBlock, maxHeight: "80px" }}>
                        <Typography sx={styles.text}>
                          {data.solution || "No solution provided"}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Plugin Output */}
                    {data.pluginOutput && (
                      <Grid item xs={12}>
                        <Typography sx={styles.sectionTitle}>🔍 Plugin Output</Typography>
                        <Box sx={styles.codeBlock}>
                          <Typography sx={styles.text}>{data.pluginOutput}</Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                )}
                {activeTab === 1 && ec2Data && renderEc2Details()}
                {activeTab === 1 && !ec2Data && datadog && renderDatadog()}
                {activeTab === 2 && renderDatadog()}
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Modal>
  );
};

export default VulnerabilityDetailsModal;
